import { useQueries, useQuery } from '@tanstack/react-query'
import { useRef } from 'react'

import type { Schemas } from '@npc/api-client'

import { api } from '../../app/api'
import { ApiFailure, call } from '../../app/errors'
import type { CheckoutRegistration } from './folder'
import type { ConversationMode } from './backend-contract'

/**
 * Lulu 工作台专用的取数钩子。
 *
 * # 为什么当初不复用 `screens/chat/queries.ts`（那一屏 2026-09-02 已删）
 *
 * 那一屏读的是同一批会话端点，但它把读到的东西投影成**旧合同**的六档阶段
 * （developing / testing / preview / release / live / fix），那是单任务发布飞轮
 * 的词汇。这一屏的合同是 12 态 + WorkPlan/WorkItem/SubAgentRun，两者不是
 * 同一个模型的两种画法，是两个模型。
 *
 * 共用一份钩子会逼着其中一边把自己的语义塞进对方的返回值里，而那种耦合
 * 的代价不是重复代码，是**改一边会悄悄改坏另一边**——两屏都在线上，
 * 而没有任何用例会同时盯着它们。
 *
 * 所以这里只共用最底下那两层：生成的 client 与 `call()` 的错误收敛。
 */

const MESSAGE_POLL_INTERVAL = 5_000
// 刚发完话那几秒用的节奏。500ms 是「人还盯着屏幕」那个尺度——
// 再快只是多打几次没有新东西的请求，再慢就又能被感觉到。
const MESSAGE_POLL_FAST = 500
const MESSAGE_PAGE = 100

export type LuluConversation = Schemas['Conversation']
export type LuluMessage = Schemas['ConversationMessage']
export type LuluParticipant = Schemas['ConversationParticipant']

/**
 * 账户级会话列表。
 *
 * 通用会话（`project_id` 为空）就在这里面——设计稿要求「直接开始」能建一条
 * 不关联项目的会话，而后端的 `GET /conversations` 正好是账户级的那一条路由。
 */
export function useLuluConversations() {
  return useQuery({
    queryKey: ['lulu-conversations'],
    queryFn: async () => call('/conversations', () => api.GET('/conversations', {})),
  })
}

/**
 * 各项目下的会话。左栏要按项目分组，而账户级那条只列不挂项目的。
 *
 * # 为什么是逐个项目问，而不是加一个「全都要」的接口
 *
 * `GET /conversations` 的契约逐字是「列出**不挂项目**的总控会话」，
 * 而项目会话只有 `GET /projects/{id}/conversations` 给得出。
 * 想一次拿全得改契约（那是流 0 的事，另开一条）。
 *
 * 逐个问的代价是 N 次请求，而 N 是他的项目数（个位数）。
 * 这条路今天就能让左栏真的分组，**而分组这件事本身不该等一次契约改动**。
 *
 * 取数失败的那个项目**整组不出现**，不是显示一组空的——
 * 「这个项目下没有对话」与「这一次没问到」是两件事，
 * 后者显示成前者会让他以为对话丢了。
 */
export function useProjectConversations(projectIds: readonly string[]) {
  return useQueries({
    queries: projectIds.map((projectId) => ({
      queryKey: ['lulu-project-conversations', projectId],
      queryFn: async () =>
        call('/projects/{projectId}/conversations', () =>
          api.GET('/projects/{projectId}/conversations', {
            params: { path: { projectId } },
          }),
        ),
    })),
  })
}

export function useLuluConversation(conversationId: string | undefined) {
  return useQuery({
    queryKey: ['lulu-conversation', conversationId],
    enabled: Boolean(conversationId),
    queryFn: async () =>
      call('/conversations/{conversationId}', () =>
        api.GET('/conversations/{conversationId}', {
          params: { path: { conversationId: conversationId as string } },
        }),
      ),
  })
}

/**
 * 会话消息，按游标一路读到底。
 *
 * 只拉第一页的话，消息超过一页之后界面就停在那儿不动了，而**它看起来是正常的**——
 * 没有报错、没有空白，只是新消息不再出现。这一类失败最贵：用户会以为
 * Lulu 不说话了。
 */
/**
 * 缓存里最早一张「发布还没定案」的结果卡。
 *
 * 判据只认 release.production_state === 'pending'：verified 与 rolled_back
 * 都是终态（后者不可逆），没有 release 的卡与它无关。
 */
/**
 * 一张「发布还没定案」的结果卡最多被重读多久：两小时。
 *
 * 生产上 pending → verified 是几十秒。几小时还没定案的候选是被放弃的
 * （rolled_back 只在 verified 之后才到得了，所以它会永远 pending），
 * 而每一次轮询都从那张卡起拉到尾——300 条之后每 5 秒 4 个请求，
 * 直到这个标签页关掉（2026-09-04 审计逮到：上一版没有上界）。
 */
export const RESULT_RESETTLE_WINDOW_MS = 2 * 60 * 60 * 1000

function hasPendingResultCard(m: LuluMessage): boolean {
  return (m.blocks ?? []).some((block) => {
    const raw = block as Record<string, unknown>
    if (raw.type !== 'result_card') return false
    const release = raw.release
    return (
      typeof release === 'object' &&
      release !== null &&
      (release as Record<string, unknown>).production_state === 'pending'
    )
  })
}

/**
 * 最新那张「发布还没定案」且还在重读窗口内的结果卡；没有就是 undefined。
 * 从尾部往前找：最新那张都超出窗口了，更早的只会更早。
 */
export function firstUnsettledResultCard(
  items: readonly LuluMessage[],
  nowMs: number = Date.now(),
): LuluMessage | undefined {
  for (let i = items.length - 1; i >= 0; i--) {
    const m = items[i]!
    if (!hasPendingResultCard(m)) continue
    const at = Date.parse(m.created_at)
    return Number.isNaN(at) || nowMs - at <= RESULT_RESETTLE_WINDOW_MS ? m : undefined
  }
  return undefined
}

/** 从尾部往回找同一 sequence 的下标——重读的那几条都在尾部附近。 */
function indexOfSequence(items: readonly LuluMessage[], sequence: number): number {
  for (let i = items.length - 1; i >= 0; i--) {
    if (items[i]!.sequence === sequence) return i
    if (items[i]!.sequence < sequence) return -1
  }
  return -1
}

/**
 * 这一刻该多久问一次新消息。
 *
 * 抽成一个函数是为了它**守得住**：写在 useQuery 的参数里，验它只能去
 * 渲染一个真组件再数请求次数——那种用例又慢又脆，而这条判断只有三个分支。
 *
 *   - 归档的会话：不问了，它不会再有新消息
 *   - 刚发完话、等回音：500ms，用户正盯着屏幕
 *   - 平时：5 秒。一条没人说话的会话，每秒问一次是白费
 */
export function messagePollInterval(archived: boolean, awaitingReply: boolean): number | false {
  if (archived) return false
  return awaitingReply ? MESSAGE_POLL_FAST : MESSAGE_POLL_INTERVAL
}

export function useLuluMessages(
  conversationId: string | undefined,
  archived: boolean,
  /**
   * 刚发完话、正在等 Lulu 接话的那几秒。
   *
   * # 为什么需要它
   *
   * 新消息只能靠轮询——SSE 那条流只推 snapshot / ledger / state，**不推消息**。
   * 而服务端一点都不慢：e2e 实测「人说一句 Lulu 真的回一句」全程 0.55 秒。
   * 也就是说用户感觉到的那个「怎么这么久」，几乎全是这 5 秒轮询间隔，
   * 与模型、与限流都没关系。
   *
   * 而 5 秒在**平时**是对的：一条没人说话的会话，每秒问一次是白费。
   * 它只在一个时刻不对——**你刚说完话、正等着回音的那几秒**。
   *
   * 所以不动那个常量，只在那个时刻把节奏调快，等到了再调回去。
   */
  awaitingReply = false,
) {
  const cache = useRef<{ id: string | undefined; items: LuluMessage[] }>({
    id: undefined,
    items: [],
  })
  return useQuery({
    queryKey: ['lulu-messages', conversationId],
    enabled: Boolean(conversationId),
    refetchInterval: messagePollInterval(archived, awaitingReply),
    queryFn: async () => {
      if (cache.current.id !== conversationId) {
        cache.current = { id: conversationId, items: [] }
      }
      // **从最早一张「发布还没定案」的结果卡起重读，不是只拉更新的。**
      //
      // 结果卡上「发布」那一格是服务端**读的时候**盖上去的（消息表 append-only，
      // 后来的 production_verified 落在 lulu_settlement_release_upgrades，
      // GET /messages 时按卡合并）。而增量拉取只要 sequence 更大的那些，
      // 已经在缓存里的那张卡永远不会被再读一次——它就停在「上线还没验完」，
      // 直到用户刷新页面。2026-09-04 生产实测两次（任务 2503fac7 / 53c8d78a：
      // 库里 deploy 行早已 production_verified，卡片一分钟后仍是 pending）。
      //
      // 所以缓存里还有 production_state=pending 的结果卡时，从那张卡的前一位起
      // 拉，拉回来的按 sequence **覆盖**回去。一张卡定案（verified / rolled_back）
      // 之后就不再重读，平时的增量语义一个字不变。重读有上界（两小时，
      // RESULT_RESETTLE_WINDOW_MS）：被放弃的候选永远 pending，不能让它把这条会话
      // 的每一次轮询都变成整段重拉。
      const tailSequence = cache.current.items[cache.current.items.length - 1]?.sequence ?? 0
      const unsettled = firstUnsettledResultCard(cache.current.items)
      let after = unsettled === undefined ? tailSequence : unsettled.sequence - 1
      for (;;) {
        const page = await call('/conversations/{conversationId}/messages', () =>
          api.GET('/conversations/{conversationId}/messages', {
            params: {
              path: { conversationId: conversationId as string },
              query: { after_sequence: after, limit: MESSAGE_PAGE },
            },
          }),
        )
        const batch = page.messages
        if (batch.length === 0) break
        // **按 sequence 单调追加，不是无脑 push。**
        //
        // 这个缓存会被并发进入：轮询一次、事件驱动的重取一次，
        // 两次 queryFn 同时跑、读到同一个 `after`、各拉回同一批，
        // 然后**都往同一个数组里 push**——屏幕上每条消息出现两遍。
        //
        // 2026-08-30 真踩到：D1 那条接线合上之后，
        // `lulu-tools` 那几条断言「三条正文」的用例收到了 6 条，正好翻倍。
        // 它在只有轮询的时候不会现形——**一个并发窗口要有两个进入者**，
        // 而在那之前只有一个。
        //
        // 判据是 sequence 而不是 id：sequence 本来就是这张表的顺序真相，
        // 按它判顺带保证了「乱序到达也不会插错位置」。
        for (const m of batch) {
          const tail = cache.current.items[cache.current.items.length - 1]
          if (tail !== undefined && m.sequence <= tail.sequence) {
            // 已经有这一条：**覆盖**，不是跳过。这是上面那次重读的另一半——
            // 重读拉回来的卡带着服务端此刻盖上去的 release，
            // 不覆盖就白读了。并发撞车时两次拉回同一条，覆盖也不会出现两遍。
            const at = indexOfSequence(cache.current.items, m.sequence)
            if (at >= 0) cache.current.items[at] = m
            continue
          }
          cache.current.items.push(m)
        }
        const last = batch[batch.length - 1]
        // 没有前进就停：后端要是回了一页 sequence 不增的东西，
        // 没有这一步就是一个每 5 秒跑一次的死循环。
        if (!last || last.sequence <= after) break
        after = last.sequence
        if (batch.length < MESSAGE_PAGE) break
      }
      return { messages: [...cache.current.items] }
    },
  })
}

/**
 * 会话参与者。消息里只有 `participant_id`，谁说的这件事要靠它解出来。
 *
 * 解不出来就**不显示名字**，而不是把那串 uuid 摆到界面上：设计稿要求每条回复
 * 标明是谁说的，一串 uuid 不是「谁」，它只是让人看见我们没解出来。
 */
export function useLuluParticipants(conversationId: string | undefined) {
  return useQuery({
    queryKey: ['lulu-participants', conversationId],
    enabled: Boolean(conversationId),
    queryFn: async () =>
      call('/conversations/{conversationId}/participants', () =>
        api.GET('/conversations/{conversationId}/participants', {
          params: { path: { conversationId: conversationId as string } },
        }),
      ),
  })
}

/**
 * 新建会话。**两条真路由，不是一条带可选参数的。**
 *
 * 后端把它们分成了两个 operationId：
 *   - `POST /conversations`                        —— 不挂项目的总控会话
 *   - `POST /projects/{projectId}/conversations`   —— 挂在项目下的
 *
 * 这正好对上「通用会话允许 project_id 为空」那条合同，而且比一个
 * `{projectId?: string}` 的可选字段更硬：**没有哪一次调用能因为漏传
 * 而意外建成通用会话**——路由本身就是那个选择。
 *
 * # mode 落库了（2026-08-26 收口）
 *
 * 上一轮这里写的是「mode 没有落库」。那句话在写下的那一刻是真的，
 * **而后端合同落地的那一刻它变成了假话**——CLAUDE.md §8 有一整条讲
 * 这件事：契约合入时要回头核一遍所有说「还没有」的话，因为
 * 「表没有」和「表有了没接」的修法完全相反。
 *
 * 现在 `CreateConversationRequest.collaboration_mode` 是真字段，
 * 取值就是界面上那两个词（solo|team），库里的 'single' 由服务端在出口
 * 翻译。所以这里直接把界面的词发出去，前端不再有第二份词表。
 */
export async function createLuluConversation(input: {
  readonly title: string
  readonly projectId: string | null
  readonly mode: ConversationMode
  readonly idempotencyKey: string
}): Promise<LuluConversation> {
  if (input.projectId === null) {
    const out = await call('/conversations', () =>
      api.POST('/conversations', {
        params: { header: { 'Idempotency-Key': input.idempotencyKey } },
        body: { title: input.title, collaboration_mode: input.mode },
      }),
    )
    return out.conversation
  }
  const out = await call('/projects/{projectId}/conversations', () =>
    api.POST('/projects/{projectId}/conversations', {
      params: {
        path: { projectId: input.projectId as string },
        header: { 'Idempotency-Key': input.idempotencyKey },
      },
      body: { title: input.title, collaboration_mode: input.mode },
    }),
  )
  return out.conversation
}

/**
 * 新建项目的三阶段真实状态（验收 #49 #50 #51）。
 *
 * **GET 天然幂等**——openapi 的描述里就是这么写的，所以「中途关页、
 * 再进来看到当时的真实阶段」不需要任何额外机制：重新拉一次就是当时的样子，
 * 不从头播一遍。
 *
 * `enabled` 由调用方控制：项目还没建出来的时候不该发这个请求，
 * 否则会拿到一个 404 然后把它显示成「失败」——而那时它只是还不存在。
 */
export function useProjectInitStatus(projectId: string | undefined) {
  return useQuery({
    queryKey: ['lulu-init-status', projectId],
    enabled: projectId !== undefined,
    queryFn: async () =>
      call('/projects/{projectId}/init-status', () =>
        api.GET('/projects/{projectId}/init-status', {
          params: { path: { projectId: projectId as string } },
        }),
      ),
  })
}

// ── 真实工作台端点（2026-08-26 收口接上）──────────────────────────

export type LuluAgent = Schemas['LuluAgent']
export type LuluAgentModelCandidate = Schemas['LuluAgentModelCandidate']
export type WorkboardSnapshot = Schemas['WorkboardSnapshot']
export type PendingDecision = Schemas['PendingDecision']
export type RecentProject = Schemas['RecentProject']
export type RepositoryCheckout = Schemas['RepositoryCheckout']

/** Agent 库。`scope` 决定看账户级还是某个项目的。 */
export function useLuluAgents(
  scope: 'all' | 'account' | 'project',
  projectId?: string,
  includeDisabled = false,
) {
  return useQuery({
    queryKey: ['lulu-agents', scope, projectId ?? null, includeDisabled],
    queryFn: async () =>
      call('/agents', () =>
        api.GET('/agents', {
          params: {
            query: projectId
              ? { scope, project_id: projectId, include_disabled: includeDisabled }
              : { scope, include_disabled: includeDisabled },
          },
        }),
      ),
  })
}

/**
 * Agent 配置弹窗的模型候选。
 *
 * 候选只来自模型库中已验证、已启用的 Model Profile；它不返回 provider
 * 凭据，也不参与输入框里的下一条消息模型选择。
 */
export function useLuluAgentModelCandidates(scope: 'account' | 'project', projectId?: string) {
  return useQuery({
    queryKey: ['lulu-agent-model-candidates', scope, projectId ?? null],
    queryFn: async () =>
      call('/agent-models', () =>
        api.GET('/agent-models', {
          params: { query: projectId ? { scope, project_id: projectId } : { scope } },
        }),
      ),
  })
}

export interface LuluAgentInput {
  readonly displayName: string
  readonly avatarRef: string
  readonly role: string
  readonly instructions: string
  readonly scope: 'account' | 'project'
  readonly projectId?: string
  readonly modelPolicy: Record<string, unknown>
  readonly luluSchedulable: boolean
}

/**
 * 编辑不必改名。尤其是系统自动命名的 Agent，保留名称字段意味着保留
 * `display_name_source=system`，而不是把同一个显示文本误标为用户自定义。
 */
export interface LuluAgentUpdateInput {
  readonly displayName?: string
  readonly avatarRef: string
  readonly role: string
  readonly instructions: string
  readonly modelPolicy: Record<string, unknown>
  readonly luluSchedulable: boolean
}

/** 新建长期 Agent。名称留空时由后端按角色稳定命名。 */
export async function createLuluAgent(input: LuluAgentInput): Promise<LuluAgent> {
  const out = await call('/agents', () =>
    api.POST('/agents', {
      body: {
        display_name: input.displayName,
        avatar_ref: input.avatarRef,
        role: input.role,
        instructions: input.instructions,
        scope: input.scope,
        ...(input.projectId ? { project_id: input.projectId } : {}),
        model_policy: input.modelPolicy,
        lulu_schedulable: input.luluSchedulable,
      },
    }),
  )
  return out.agent
}

/** 编辑 Agent 配置。运行中的 Session 固定旧 revision，不会被这里打断。 */
export async function updateLuluAgent(
  agentId: string,
  input: LuluAgentUpdateInput,
): Promise<LuluAgent> {
  const out = await call('/agents/{agentId}', () =>
    api.PATCH('/agents/{agentId}', {
      params: { path: { agentId } },
      body: {
        ...(input.displayName === undefined ? {} : { display_name: input.displayName }),
        avatar_ref: input.avatarRef,
        role: input.role,
        instructions: input.instructions,
        model_policy: input.modelPolicy,
        lulu_schedulable: input.luluSchedulable,
      },
    }),
  )
  return out.agent
}

/** 停用账户 Agent。后端软删并幂等，历史会话不受影响。 */
export async function disableLuluAgent(agentId: string): Promise<void> {
  await call('/agents/{agentId}', () =>
    api.DELETE('/agents/{agentId}', {
      params: { path: { agentId } },
    }),
  )
}

/** 会话的对话模型。**只读这一条，团队成员的模型在 Agent 那边。** */
export function useComposerModel(conversationId: string | undefined) {
  return useQuery({
    queryKey: ['lulu-composer-model', conversationId],
    enabled: conversationId !== undefined,
    queryFn: async () =>
      call('/conversations/{conversationId}/model', () =>
        api.GET('/conversations/{conversationId}/model', {
          params: { path: { conversationId: conversationId as string } },
        }),
      ),
  })
}

/** 工作板。子 Agent 在 `sessions` 里，工作项在 `work_items` 里。 */
export function useWorkboard(conversationId: string | undefined) {
  return useQuery({
    queryKey: ['lulu-workboard', conversationId],
    enabled: conversationId !== undefined,
    queryFn: async () =>
      call('/conversations/{conversationId}/workboard', () =>
        api.GET('/conversations/{conversationId}/workboard', {
          params: { path: { conversationId: conversationId as string } },
        }),
      ),
  })
}

/** 待决定。红级那五类都从这里来。 */
export function usePendingDecisions() {
  return useQuery({
    queryKey: ['lulu-decisions'],
    queryFn: async () => call('/decisions', () => api.GET('/decisions', {})),
  })
}

/**
 * 最近的远程项目。
 *
 * `synced` 为 `null` 表示**未核验**——openapi 那段注释写死了「平台没有
 * 同步核验来源前恒为 null，不许伪造 true/false」。启动器据此写「未核验」，
 * 而不是画一个绿点。
 */
export function useRecentProjects(limit = 6) {
  return useQuery({
    queryKey: ['lulu-recent-projects', limit],
    queryFn: async () =>
      call('/projects/recent', () => api.GET('/projects/recent', { params: { query: { limit } } })),
  })
}

/** 项目的本地 checkout 登记（macOS 用；Web 上没有入口调它）。 */
/**
 * 把一条通用会话绑到项目上。**一次性，绑上不能换**（后端合同：再绑回 409）。
 *
 * 不在这里吞 409：调用方要能把「已经绑过了」和「绑失败了」分开说——
 * 前者是一句陈述，后者要给重试。合成一句的话，用户会对着一条绑好的
 * 会话反复点「再试一次」。
 */
export async function bindConversationProject(input: {
  readonly conversationId: string
  readonly projectId: string
}): Promise<void> {
  await call('/conversations/{conversationId}/bind', () =>
    api.POST('/conversations/{conversationId}/bind', {
      params: { path: { conversationId: input.conversationId } },
      body: { project_id: input.projectId },
    }),
  )
}

/**
 * 把一位 Agent 加进这条会话的团队（画板 teamEmpty 每行的「加入团队」）。
 *
 * **只发 agent_profile_id。** 身份（executor_id / display_name）由服务端从
 * 那个 profile 派生——浏览器给不了，也不该给：收下客户端给的身份，
 * 等于任何有 operate 权限的人都能以别人的名义进会话。
 *
 * 加进来之后**不改全局 Agent 库**，只作用于这条会话。
 */
export async function joinConversationTeam(input: {
  readonly conversationId: string
  readonly agentProfileId: string
}): Promise<void> {
  await call('/conversations/{conversationId}/agents', () =>
    api.POST('/conversations/{conversationId}/agents', {
      params: { path: { conversationId: input.conversationId } },
      body: { agent_profile_id: input.agentProfileId },
    }),
  )
}

/**
 * 把一位**已有成员**叫进这条会话（L-58）。
 *
 * **只发 user_id。** 与上面那条同一条边界：浏览器说的是「叫谁进来」，
 * 不是「他是谁」——显示名与 executor_id 由服务端从那条用户记录派生。
 * user_id 会被拿去成员表里核，不是本账户成员就是 404。
 *
 * 叫进来**不给他任何权限**，只决定他看不看得见这条会话。
 */
export async function addConversationPerson(input: {
  readonly conversationId: string
  readonly userId: string
}): Promise<void> {
  await call('/conversations/{conversationId}/people', () =>
    api.POST('/conversations/{conversationId}/people', {
      params: { path: { conversationId: input.conversationId } },
      body: { user_id: input.userId },
    }),
  )
}

/**
 * 把一个人移出这条会话。
 *
 * 服务端写 `removed_at` 而不是删行——他说过的话挂在那条记录上。
 * 移出去只是「他不再看得见这条会话」，**不动他的账户角色**。
 */
export async function removeConversationPerson(input: {
  readonly conversationId: string
  readonly userId: string
}): Promise<void> {
  await call('/conversations/{conversationId}/people/{userId}', () =>
    api.DELETE('/conversations/{conversationId}/people/{userId}', {
      params: { path: { conversationId: input.conversationId, userId: input.userId } },
    }),
  )
}

export function useProjectCheckouts(projectId: string | undefined) {
  return useQuery({
    queryKey: ['lulu-checkouts', projectId],
    enabled: projectId !== undefined,
    queryFn: async () =>
      call('/projects/{projectId}/checkouts', () =>
        api.GET('/projects/{projectId}/checkouts', {
          params: { path: { projectId: projectId as string } },
        }),
      ),
  })
}

/**
 * 登记一个本机文件夹与项目的绑定（仅 macOS）。
 *
 * **绝对路径不出设备**（PRD §3.6）：指纹与末段目录名由
 * `buildCheckoutRegistration` 在设备侧算好，这里只负责把它发出去。
 * 这个函数刻意收 `CheckoutRegistration` 而不是 `{path, projectId}`——
 * 收后者的话，路径就会在这一层出现，而这一层离网络只差一行。
 *
 * 同指纹同项目 = 幂等 200；同指纹异项目 = 409 `folder_bound_elsewhere`，
 * 由调用方翻成 `taken`（**不静默抢占**，验收 #55）。
 */
export async function registerCheckout(reg: CheckoutRegistration): Promise<RepositoryCheckout> {
  const out = await call('/environments/checkout', () =>
    api.POST('/environments/checkout', { body: reg }),
  )
  return out.checkout
}

/**
 * 发一条消息。
 *
 * `idempotencyKey` 由调用方给且**重试时必须原样带回来**——
 * `ConversationMessageEnvelope.replayed` 为真表示这次是幂等重放，
 * 没有新建消息。没有这把钥匙，一次「发出去了但响应丢了」的重试
 * 会在会话里留下两条一模一样的话，而用户只说了一遍。
 */
export async function sendLuluMessage(input: {
  readonly conversationId: string
  readonly body: string
  readonly idempotencyKey: string
  /**
   * 这一条要带走的附件（**已经传好的那些的服务端 id**）。
   *
   * 服务端在发消息的同一个事务里认领它们，一个挂不上就整笔拒绝——
   * 所以这里只许放 `state === 'done' && serverId` 那些
   * （attachments.ts 的 sendableIDs 就是干这个的）。
   */
  readonly attachmentIDs?: readonly string[]
  /** 他这一条选的推理强度。不给就不发这个字段，服务端按功能位默认。 */
  readonly reasoningEffort?: 'fast' | 'standard' | 'max'
  /** 「这一条只出计划，别开工」。假就不发这个字段。 */
  readonly noAutoStart?: boolean
  /**
   * 他在这句话里 @ 了谁（结构化，带字节偏移）。
   *
   * **不发的话 @ 就只是三个字**：服务端存得下、校验通得过、回话循环
   * 看不见——2026-09-06 之前就是这样，@ 谁回话的都是 Lulu。
   */
  readonly mentions?: readonly { readonly executor_id: string; readonly offset: number; readonly length: number }[]
}): Promise<{ readonly message: LuluMessage; readonly replayed: boolean }> {
  const out = await call('/conversations/{conversationId}/messages', () =>
    api.POST('/conversations/{conversationId}/messages', {
      params: {
        path: { conversationId: input.conversationId },
        header: { 'Idempotency-Key': input.idempotencyKey },
      },
      // 空的时候不发这个字段。
      //
      // **这一条对幂等是中性的**：服务端那侧只在非空时把附件折进摘要
      // （postgres.go 那段，与 blocks 同一个写法），所以 `[]` 与缺席
      // 算出来的摘要是同一个。发不发都不会让重放被判成两件事。
      //
      // 不发只是让请求体保持原来的形状：一条不带附件的消息，
      // 在网络面板里应该与这个功能上线之前长得一模一样。
      body: {
        body: input.body,
        ...(input.attachmentIDs && input.attachmentIDs.length > 0
          ? { attachment_ids: [...input.attachmentIDs] }
          : {}),
        ...(input.reasoningEffort ? { reasoning_effort: input.reasoningEffort } : {}),
        ...(input.noAutoStart ? { no_auto_start: true } : {}),
        // 与附件那条同一个理由：空的时候不发，请求体保持原来的形状。
        ...(input.mentions && input.mentions.length > 0
          ? { mentions: input.mentions.map((m) => ({ ...m })) }
          : {}),
      },
    }),
  )
  return { message: out.message, replayed: out.replayed }
}

/**
 * 定住这条会话的对话模型（或者放回自动）。
 *
 * **只管对话，不碰团队。** 设计稿 W16b 那两行「团队里 4 位 Agent
 * 用的模型，一个都不变」「正在跑的活不打断，也不换」说的就是这条：
 * 它写的是会话的 composer 策略，不是任何一位 Agent 的配置。
 */
export async function setComposerModel(input: {
  readonly conversationId: string
  readonly policy: 'auto' | 'fixed'
  readonly profileId?: string
  /** 幂等键。重试时原样带回来，别现生成一把。 */
  readonly idempotencyKey: string
}): Promise<{
  readonly policy: 'auto' | 'fixed'
  readonly profile_id?: string
  readonly model_id?: string
}> {
  const out = await call('/conversations/{conversationId}/model', () =>
    api.POST('/conversations/{conversationId}/model', {
      // 这条 handler 与会话消息端点都直接读取 Idempotency-Key。
      // OpenAPI 也使用这条端点自己的参数，不能误引用全局的 X- 前缀组件。
      params: {
        path: { conversationId: input.conversationId },
        header: { 'Idempotency-Key': input.idempotencyKey },
      },
      body:
        input.policy === 'fixed' && input.profileId
          ? { policy: 'fixed' as const, profile_id: input.profileId }
          : { policy: 'auto' as const },
    }),
  )
  return out.composer_model
}

/**
 * 回答一个红级决定。
 *
 * `approve` 是**用户按的那一下**，不是我们替他算出来的判断。
 * 过期的卡不该走到这里——调用方先看 `isExpired`，因为
 * 「过期了还提交」与「过期了重新问」是两件完全不同的事，
 * 而后端只知道我们发了什么。
 */
export async function answerDecision(input: {
  readonly decisionId: string
  readonly approve: boolean
}): Promise<void> {
  await call('/decisions/{decisionId}/answer', () =>
    api.POST('/decisions/{decisionId}/answer', {
      params: { path: { decisionId: input.decisionId } },
      body: { approve: input.approve },
    }),
  )
}

/**
 * 用一个名字新建项目（启动器「用这个名字新建」）。
 *
 * 409 表示 slug 撞了——**那不是错误，是「这个名字已经有人用了」**，
 * 调用方要把它说成一句人话，而不是「操作失败」。
 */
export async function createProjectNamed(
  name: string,
  idempotencyKey?: string,
): Promise<{ readonly id: string }> {
  // **带上钥匙才走幂等账本。** 验收 #51 要的「重复点重试不会建出第二个项目」
  // 一直只活在 `stageIdempotencyKey` 那个纯函数里——它算得出一把键，
  // 而**那把键从来没有上过网**。服务端 `routes.go` 早就在读
  // `Idempotency-Key` 这个头了，缺的只是这一侧把它发出去。
  //
  // 这是「只有用例用过的接口，等于没被用过」的另一种形状：函数被用过、
  // 有用例、产物里也有它，只是它的产出没有到达任何人。
  const out = await call('/projects', () =>
    api.POST('/projects', {
      body: { name },
      ...(idempotencyKey ? { params: { header: { 'Idempotency-Key': idempotencyKey } } } : {}),
    }),
  )
  return { id: out.id }
}

/**
 * 子 Agent 失败之后的三条出路（设计稿 S04）。
 *
 * 三条走同一个端点，因为它们共用一整组前置判断（同一个 parent、
 * 同一个工作包、状态机允许），而那组判断在服务端一个事务里做完。
 *
 * **`swap_model` 必须带 `runtimeAdapter`**——允许空等于允许
 * 「换成和原来一样的」，而那是 retry 不是 swap。
 */
export async function recoverSubrun(input: {
  readonly subrunId: string
  readonly action: 'retry' | 'swap_model' | 'takeover'
  readonly runtimeAdapter?: string
  readonly idempotencyKey: string
}): Promise<{ readonly takeover: boolean; readonly replayed: boolean }> {
  const out = await call('/subruns/{subrunId}/recovery', () =>
    api.POST('/subruns/{subrunId}/recovery', {
      params: {
        path: { subrunId: input.subrunId },
        header: { 'Idempotency-Key': input.idempotencyKey },
      },
      body:
        input.action === 'swap_model'
          ? { action: 'swap_model' as const, runtime_adapter: input.runtimeAdapter ?? '' }
          : { action: input.action },
    }),
  )
  return { takeover: out.takeover === true, replayed: out.replayed === true }
}

/**
 * 停掉一个临时子 Agent。服务端按 subrun 幂等取消，父 Agent 和兄弟运行不受影响。
 */
export async function cancelLuluSubrun(subrunId: string): Promise<void> {
  await call('/subruns/{subrunId}', () =>
    api.DELETE('/subruns/{subrunId}', {
      params: { path: { subrunId } },
    }),
  )
}

/** 一次「停下这一轮」到底停下了什么。数字来自服务端，不是这一侧数出来的。 */
export interface CancelRunOutcome {
  readonly cancelledItems: number
  readonly cancelledSessions: number
  readonly stoppedRuntimes: number
  /** Lulu 正在生成的那句回话有没有被一起叫停（一轮纯对话点「停下」时只有这一格会变）。 */
  readonly stoppedReply: boolean
  /** 这一次是同一把钥匙的重放，服务端回的是第一次那笔的数字。 */
  readonly replayed: boolean
}

/**
 * 停下这一轮。
 *
 * # 它与 cancelLuluSubrun 停的不是同一个东西
 *
 * `cancelLuluSubrun` 停一个子 Agent。而一件刚派下去、或者 blocked 的活
 * **一个子 Agent 都没有**，工作板上它照样占一行——逐条停子 Agent 的话，
 * 那些行会原样留在原地，屏幕上「都停了」而事实上没停。
 *
 * 这一条的判据在**工作包**上：这一轮里还没做完的一个不剩，
 * 绑在它们上的执行会话一并收口，服务端还会去把执行器真的停掉
 * （development 那种就是撤掉绑着的 DevelopmentTask）。
 *
 * # 钥匙不在这一层发
 *
 * 调用方给。理由在 cancel-run-key.ts：这把钥匙什么时候复用、什么时候换，
 * 取决于上一次的结局，而这一层看不见上一次。
 */
export async function cancelConversationRun(input: {
  readonly conversationId: string
  readonly idempotencyKey: string
  readonly reason?: string
  /**
   * 停多大一片。`turn` = 只停他最后一句话之后派出来的（等回话那一行的「停下」与 Esc）；
   * `all`（缺省）= 这条话头里所有没做完的活（工作板那颗按钮）。正在生成的那句回话两档都停。
   */
  readonly scope?: 'all' | 'turn'
}): Promise<CancelRunOutcome> {
  const body = {
    ...(input.reason === undefined ? {} : { reason: input.reason }),
    ...(input.scope === undefined ? {} : { scope: input.scope }),
  }
  const out = await call('/conversations/{conversationId}/cancel', () =>
    api.POST('/conversations/{conversationId}/cancel', {
      params: {
        path: { conversationId: input.conversationId },
        header: { 'Idempotency-Key': input.idempotencyKey },
      },
      ...(Object.keys(body).length === 0 ? {} : { body }),
    }),
  )
  return {
    cancelledItems: out.cancelled_items,
    cancelledSessions: out.cancelled_sessions,
    stoppedRuntimes: out.stopped_runtimes,
    replayed: out.replayed === true,
    stoppedReply: out.stopped_reply === true,
  }
}

/**
 * 撤回这次安排。
 *
 * # 撤的是任务不是工作项
 *
 * 工作项是「看得见的那一行」，DevelopmentTask 才是「真的有人在做的那件事」。
 * 撤工作项只会让板上少一行，而那件活照样在跑——那是最坏的一种撤销：
 * 他以为停了，实际没停。
 *
 * # 409 不是「出错了」
 *
 * 契约里写着「与执行状态迁移发生竞争时返回 409，避免悄悄覆盖较新的状态」——
 * 也就是「你晚了一步，它已经跑完/失败了」。那与「请求坏了」的下一个动作不一样：
 * 前者去看现在到哪了，后者重试。所以这里把它单独抛出来。
 */
export class UndoTooLateError extends Error {
  constructor() {
    super('undo raced a state transition')
    this.name = 'UndoTooLateError'
  }
}

// ── 会话附件（迁移 000147）──────────────────────────────────────────

export type ConversationAttachment = Schemas['ConversationAttachment']

/** 传上来了还没发出去的那些。界面重进时用它接管，不然它们永远躺在库里。 */
export function usePendingAttachments(conversationId: string | undefined) {
  return useQuery({
    queryKey: ['lulu-pending-attachments', conversationId],
    enabled: Boolean(conversationId),
    queryFn: async () =>
      call('/conversations/{conversationId}/attachments', () =>
        api.GET('/conversations/{conversationId}/attachments', {
          params: { path: { conversationId: conversationId as string } },
        }),
      ),
  })
}

/**
 * 传一个文件上去。**用 XHR 而不是 fetch。**
 *
 * fetch 没有上传进度（ReadableStream 的请求体上传进度在浏览器里至今不通用），
 * 而进度是这一块存在的理由之一：一个 8MB 的文件在 4G 上要十几秒，
 * 没有进度的那十几秒里用户不知道它是在传还是卡死了。
 *
 * 生成的客户端也不走 multipart，所以这条**刻意手写**，
 * 而不是硬把 api.POST 掰成一个它不擅长的形状。
 */
export function uploadAttachment(input: {
  readonly conversationId: string
  readonly file: File
  readonly onProgress?: (percent: number) => void
  readonly signal?: AbortSignal
}): Promise<ConversationAttachment> {
  return new Promise((resolve, reject) => {
    const form = new FormData()
    form.append('file', input.file)
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `/api/v1/conversations/${input.conversationId}/attachments`)
    // 与生成客户端同一套凭证策略：同源 cookie。
    xhr.withCredentials = true
    xhr.upload.onprogress = (e) => {
      if (!e.lengthComputable || input.onProgress === undefined) return
      // **不许到 100**：字节发完不等于服务端收下了（它还要过三道防线）。
      // 让它停在 99，真的成了再由调用方置 100——否则进度条会先满、
      // 再变成「没传上去」，而那一秒用户以为已经成了。
      input.onProgress(Math.min(99, Math.round((e.loaded / e.total) * 100)))
    }
    xhr.onerror = () => reject(new Error('upload failed'))
    xhr.onabort = () => reject(new DOMException('aborted', 'AbortError'))
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText).attachment as ConversationAttachment)
        } catch {
          reject(new Error('bad response'))
        }
        return
      }
      // 服务端那句人话原样带出去。**读不出来就不编一句**——
      // 编出来的原因会把用户送去改一个没问题的地方。
      let message = ''
      try {
        message = String(JSON.parse(xhr.responseText).user_message ?? '')
      } catch {
        message = ''
      }
      reject(new Error(message))
    }
    input.signal?.addEventListener('abort', () => xhr.abort())
    xhr.send(form)
  })
}

/** 摘掉一个还没发出去的附件。已经发出去的会 404——那是对的。 */
export async function deleteAttachment(
  conversationId: string,
  attachmentId: string,
): Promise<void> {
  await call('/conversations/{conversationId}/attachments/{attachmentId}', () =>
    api.DELETE('/conversations/{conversationId}/attachments/{attachmentId}', {
      params: { path: { conversationId, attachmentId } },
    }),
  )
}

/**
 * 这条会话此刻那份算好的计划（「开始做」那张卡）。
 *
 * `enabled` 只在挂了项目时打开：计划是按项目算的，通用会话上发这个请求
 * 拿到的是 409，而那个 409 在界面上会被读成「出错了」——
 * 它其实是「这条会话没有这回事」。
 *
 * 轮询跟着消息走：Lulu 算完计划才会有内容，而那与它回话是同一时刻。
 */
export function useConversationPlan(conversationId: string | undefined, projectId: string | null) {
  return useQuery({
    queryKey: ['lulu-plan', conversationId],
    enabled: Boolean(conversationId) && projectId !== null,
    refetchInterval: 5000,
    queryFn: async () =>
      call('/conversations/{conversationId}/plan', () =>
        api.GET('/conversations/{conversationId}/plan', {
          params: { path: { conversationId: conversationId as string } },
        }),
      ),
  })
}

/**
 * 「开始做」：真的把这一轮安排下去。
 *
 * **不带 developer_model**——那个下拉是项目详情那套聊天上的东西，
 * /lulu 这一屏没有它。空 body 就是「按默认来」，服务端那侧显式收这一种
 * （`r.ContentLength != 0` 才解析），所以这里发一个 `{}` 也不必。
 *
 * 回的 task_id 立刻就有用：工作板要刷、撤回那颗按钮要认它。
 */
export async function scheduleDevelopment(
  conversationId: string,
): Promise<{ readonly taskId: string }> {
  const out = await call('/conversations/{conversationId}/schedule', () =>
    api.POST('/conversations/{conversationId}/schedule', {
      params: { path: { conversationId } },
      body: {},
    }),
  )
  return { taskId: out.task_id }
}

export async function undoScheduledTask(taskId: string): Promise<void> {
  try {
    await call('/development-tasks/{taskId}/cancel', () =>
      api.POST('/development-tasks/{taskId}/cancel', { params: { path: { taskId } } }),
    )
  } catch (err) {
    if (err instanceof ApiFailure && err.status === 409) throw new UndoTooLateError()
    throw err
  }
}

/** Lulu 的技能：服务端注册表的投影 + 账号级开关 + 最近一次跑。 */
export type LuluSkill = Schemas['LuluSkill']

export function useLuluSkills() {
  return useQuery({
    queryKey: ['lulu-skills'],
    queryFn: async () => call('/skills', () => api.GET('/skills')),
  })
}

/**
 * 给这条话头改名（PATCH /conversations/{id}）。
 *
 * 回改完的会话。调用方拿它去覆盖缓存里那一条，再让会话栏重取——
 * 名字在两处出现，只改一处是「改了一半」。
 */
export async function renameConversation(conversationId: string, title: string) {
  const out = await call('/conversations/{conversationId}', () =>
    api.PATCH('/conversations/{conversationId}', {
      params: { path: { conversationId } },
      body: { title },
    }),
  )
  return out.conversation
}

/** 拧一个技能的开关（账号级）。回拧完之后这个技能的样子。 */
export async function setLuluSkillEnabled(key: string, enabled: boolean): Promise<LuluSkill> {
  const out = await call('/skills/{skillKey}', () =>
    api.PUT('/skills/{skillKey}', {
      params: { path: { skillKey: key } },
      body: { enabled },
    }),
  )
  return out.skill
}

/** 早晨留言每一项那张卡上的动作（需求稿 V2 §7.4）。`why` 不是服务端动作：卡上的 detail 就是。 */
export type BriefAction = 'schedule' | 'later' | 'ignore' | 'mute'

export type BriefActionOutcome = {
  readonly status: string
  readonly said: string
  /** schedule 那一档：贴进了哪条话头（留言在总话头里，计划出在项目话头里）。 */
  readonly conversationId?: string
  readonly messageId?: string
  readonly replayed: boolean
}

export async function actOnBriefItem(input: {
  readonly conversationId: string
  readonly itemKind: 'finding' | 'proposal'
  readonly itemId: string
  readonly action: BriefAction
}): Promise<BriefActionOutcome> {
  const out = await call('/conversations/{conversationId}/brief-actions', () =>
    api.POST('/conversations/{conversationId}/brief-actions', {
      params: { path: { conversationId: input.conversationId } },
      body: { item_kind: input.itemKind, item_id: input.itemId, action: input.action },
    }),
  )
  return {
    status: out.status,
    said: out.said,
    ...(out.conversation_id === undefined ? {} : { conversationId: out.conversation_id }),
    ...(out.message_id === undefined ? {} : { messageId: out.message_id }),
    replayed: out.replayed === true,
  }
}

/** 暂停 / 继续这条话头：话照说，只是拆出来的计划不开工、没领走的活不派（迁移 000185）。 */
export async function pauseConversation(conversationId: string): Promise<LuluConversation> {
  const out = await call('/conversations/{conversationId}/pause', () =>
    api.POST('/conversations/{conversationId}/pause', { params: { path: { conversationId } } }),
  )
  return out.conversation
}

export async function resumeConversation(conversationId: string): Promise<LuluConversation> {
  const out = await call('/conversations/{conversationId}/resume', () =>
    api.POST('/conversations/{conversationId}/resume', { params: { path: { conversationId } } }),
  )
  return out.conversation
}
